import React from 'react';
import MetricCard from './MetricCard';
import styles from '../../../../styles/PerformanceDashboard.module.css';
import { UserPerformanceData } from '../types';

interface MetricsGridProps {
  performanceData: UserPerformanceData;
}

const MetricsGrid: React.FC<MetricsGridProps> = ({ performanceData }) => {
  // Helper function to format trend percentage
  const formatTrend = (trendValue: number | undefined): { value: string; isPositive: boolean } | undefined => {
    if (trendValue === undefined || trendValue === null) return undefined;
    const isPositive = trendValue >= 0;
    const absValue = Math.abs(trendValue);
    return {
      value: `${absValue.toFixed(1)}%`,
      isPositive,
    };
  };

  const trends = performanceData.trends;

  const metrics = [
    {
      title: 'Tasks Completed',
      value: performanceData.statistics.tasks.completed,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 11l3 3L22 4"></path>
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
        </svg>
      ),
      subtext: 'Platform members',
      trend: formatTrend(trends?.tasks_completed),
    },
    {
      title: 'Total Points',
      value: performanceData.statistics.points.total.toLocaleString(),
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
      ),
      subtext: 'Points earned',
      trend: formatTrend(trends?.total_points),
    },
    {
      title: 'Completion Rate',
      value: `${performanceData.statistics.tasks.completion_rate.toFixed(1)}%`,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      subtext: 'Task completion',
      trend: formatTrend(trends?.completion_rate),
    },
    {
      title: 'Approval Rate',
      value: `${performanceData.statistics.approvals.approval_rate.toFixed(1)}%`,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
      ),
      subtext: 'Approvals processed',
      trend: formatTrend(trends?.approval_rate),
    },
    {
      title: 'Tasks In Progress',
      value: performanceData.statistics.tasks.in_progress,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      subtext: 'Currently active',
      trend: formatTrend(trends?.in_progress),
    },
    {
      title: 'Pending Tasks',
      value: performanceData.statistics.tasks.pending,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      subtext: 'Needs attention',
      subtextWarning: true,
      trend: formatTrend(trends?.pending),
    },
    {
      title: 'Self-Assigned',
      value: performanceData.statistics.tasks.self_assigned,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="8.5" cy="7" r="4"></circle>
          <line x1="20" y1="8" x2="20" y2="14"></line>
          <line x1="23" y1="11" x2="17" y2="11"></line>
        </svg>
      ),
      subtext: 'Self-assigned tasks',
      iconColor: '#10b981',
    },
    {
      title: 'Avg Points/Task',
      value: performanceData.efficiency.avg_points_per_task.toFixed(1),
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
      ),
      subtext: 'Average points per task',
      iconColor: '#ef4444',
    },
  ];

  return (
    <div className={styles.metricsGrid}>
      {metrics.map((metric, index) => (
        <MetricCard
          key={index}
          title={metric.title}
          value={metric.value}
          icon={metric.icon}
          subtext={metric.subtext}
          subtextWarning={metric.subtextWarning}
          trend={metric.trend}
          iconColor={metric.iconColor}
        />
      ))}
    </div>
  );
};

export default MetricsGrid;

